import React, { useRef, useState, useEffect } from "react";
import { Box, Typography, Container, Grid } from "@mui/material";
import { motion } from "framer-motion";

const useOnScreen = (ref, rootMargin = "0px") => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setIsVisible(true);
          setHasAnimated(true);
        }
      },
      { rootMargin }
    );

    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); };
  }, [ref, rootMargin, hasAnimated]);

  return isVisible;
};

const CountUpAnimation = ({ target, suffix = "", inView }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;

    const numericTarget =
      typeof target === "string" ? parseFloat(target) : target;
    if (isNaN(numericTarget)) return;

    let start = 0;
    const duration = 2000;
    const steps = 60;
    const increment = numericTarget / steps;
    const stepTime = duration / steps;

    const timer = setInterval(() => {
      start += increment;
      if (start >= numericTarget) {
        setCount(numericTarget);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, stepTime);

    return () => clearInterval(timer);
  }, [inView, target]);

  return (
    <span>
      {count}
      {suffix}
    </span>
  );
};

const AboutSec4 = () => {
  const sectionRef = useRef(null);
  const badgeRef = useRef(null);
  const headingRef = useRef(null);

  const sectionVisible = useOnScreen(sectionRef);
  const badgeVisible = useOnScreen(badgeRef);
  const headingVisible = useOnScreen(headingRef);

  const stats = [
    {
      value: 3,
      suffix: "x",
      label: "Faster Operations",
      description:
        "Clients boost workflow speed after switching to our intelligent systems.",
    },
    {
      value: 20,
      suffix: "+",
      label: "Successful AI Deployments",
      description:
        "Built, launched, and maintained across multiple industries.",
    },
    {
      value: 90,
      suffix: "%",
      label: "Client Satisfaction",
      description:
        "Driven by transparency, consistent delivery, and measurable results.",
    },
    {
      value: 10,
      suffix: "+",
      label: "Long Term Partners",
      description:
        "Organizations that grow with us year after year.",
    },
    {
      value: "24/7",
      suffix: "",
      label: "Support",
      description:
        "A dedicated team monitoring performance, uptime, and data health.",
      isText: true,
    },
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut",
      },
    },
  };

  return (
    <Box
      ref={sectionRef}
      sx={{
        width: "100%",
        background: "#FFFFFF",
        py: { xs: 6, md: 10 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        {/* Badge */}
        <motion.div
          ref={badgeRef}
          initial="hidden"
          animate={badgeVisible ? "visible" : "hidden"}
          variants={fadeInUp}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <motion.div
              animate={
                badgeVisible
                  ? {
                      boxShadow: [
                        "0 0 0px rgba(64, 122, 255, 0)",
                        "0 0 18px rgba(64, 122, 255, 0.25)",
                        "0 0 0px rgba(64, 122, 255, 0)",
                      ],
                    }
                  : {}
              }
              transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              style={{ borderRadius: "20px" }}
            >
              <Typography
                sx={{
                  backgroundColor: "#f0f7ff",
                  color: "#1a73e8",
                  px: 3,
                  py: 1,
                  borderRadius: "20px",
                  fontSize: { xs: "12px", md: "14px" },
                  fontWeight: 600,
                  fontFamily: "Poppins",
                  textTransform: "uppercase",
                  letterSpacing: "1.5px",
                  border: "1px solid #e0ecff",
                }}
              >
                Why Calebs.
              </Typography>
            </motion.div>
          </Box>
        </motion.div>

        {/* Heading */}
        <motion.div
          ref={headingRef}
          initial="hidden"
          animate={headingVisible ? "visible" : "hidden"}
          variants={fadeInUp}
        >
          <Typography
            variant="h2"
            sx={{
              textAlign: "center",
              color: "#1a73e8",
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: { xs: "1rem", sm: "1.5rem", md: "2rem" },
              mb: 1.5,
              lineHeight: 1.3,
            }}
          >
            Built for accuracy | Designed for scale
          </Typography>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={headingVisible ? "visible" : "hidden"}
          variants={{
            hidden: { opacity: 0, y: 30 },
            visible: {
              opacity: 1,
              y: 0,
              transition: { duration: 0.7, delay: 0.15, ease: "easeOut" },
            },
          }}
        >
          <Typography
            variant="h2"
            sx={{
              textAlign: "center",
              color: "#1a73e8",
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: { xs: "1rem", sm: "1.5rem", md: "2rem" },
              mb: 6,
              lineHeight: 1.3,
            }}
          >
            Trusted for outcomes
          </Typography>
        </motion.div>

        {/* Top Row — 3 Cards */}
        <Grid
          container
          spacing={3}
          justifyContent="center"
          sx={{ mb: 3 }}
        >
          {stats.slice(0, 3).map((stat, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <StatCard
                stat={stat}
                index={index}
                delay={0.2 + index * 0.15}
                sectionVisible={sectionVisible}
              />
            </Grid>
          ))}
        </Grid>

        {/* Bottom Row — 2 Cards Centered */}
        <Grid
          container
          spacing={3}
          justifyContent="center"
        >
          {stats.slice(3).map((stat, index) => (
            <Grid item xs={12} sm={6} md={4} key={index + 3}>
              <StatCard
                stat={stat}
                index={index + 3}
                delay={0.2 + (index + 3) * 0.15}
                sectionVisible={sectionVisible}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

const StatCard = ({ stat, index, delay, sectionVisible }) => {
  const cardRef = useRef(null);
  const cardVisible = useOnScreen(cardRef);

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 40, scale: 0.96 }}
      animate={
        cardVisible
          ? {
              opacity: 1,
              y: 0,
              scale: 1,
              transition: {
                duration: 0.6,
                delay: delay,
                ease: "easeOut",
              },
            }
          : {}
      }
      style={{ height: "100%" }}
    >
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "16px",
          p: { xs: 3, md: 3.5 },
          height: "100%",
          border: "1px solid #e0ecff",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          transition: "all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)",
          position: "relative",
          overflow: "hidden",
          "&:hover": {
            transform: "translateY(-8px)",
            boxShadow: "0 16px 48px rgba(64, 122, 255, 0.12)",
            borderColor: "#407aff",
          },
          "&:hover .stat-value": {
            transform: "scale(1.05)",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "3px",
            background:
              "linear-gradient(90deg, transparent, #407aff, #1a73e8, transparent)",
            opacity: 0,
            transition: "opacity 0.4s ease",
          },
          "&:hover::after": {
            opacity: 1,
          },
        }}
      >
        {/* Value */}
        <Box>
          <motion.div
            initial={{ opacity: 0, x: -15 }}
            animate={
              cardVisible
                ? { opacity: 1, x: 0 }
                : {}
            }
            transition={{ duration: 0.5, delay: delay + 0.2 }}
          >
            <Typography
              className="stat-value"
              sx={{
                color: "#1a73e8",
                fontFamily: "Poppins",
                fontWeight: 800,
                fontSize: { xs: "2rem", sm: "2.4rem", md: "2.8rem" },
                lineHeight: 1.2,
                mb: 0.5,
                transition: "transform 0.3s ease",
              }}
            >
              {stat.isText ? (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={cardVisible ? { opacity: 1 } : {}}
                  transition={{ duration: 0.8, delay: delay + 0.3 }}
                >
                  24/7
                </motion.span>
              ) : (
                <CountUpAnimation
                  target={stat.value}
                  suffix={stat.suffix}
                  inView={cardVisible}
                />
              )}
            </Typography>
          </motion.div>

          {/* Label */}
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={
              cardVisible
                ? { opacity: 1, x: 0 }
                : {}
            }
            transition={{ duration: 0.5, delay: delay + 0.3 }}
          >
            <Typography
              sx={{
                color: "#455a64",
                fontFamily: "Poppins",
                fontWeight: 600,
                fontSize: { xs: "14px", md: "16px" },
                mb: 2,
                lineHeight: 1.4,
              }}
            >
              {stat.label}
            </Typography>
          </motion.div>
        </Box>

        {/* Divider */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={cardVisible ? { scaleX: 1 } : {}}
          transition={{ duration: 0.5, delay: delay + 0.4 }}
          style={{ transformOrigin: "left" }}
        >
          <Box
            sx={{
              width: "40px",
              height: "2px",
              background:
                "linear-gradient(90deg, #407aff, #e0ecff)",
              mb: 2,
            }}
          />
        </motion.div>

        {/* Description */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={
            cardVisible
              ? { opacity: 1, y: 0 }
              : {}
          }
          transition={{ duration: 0.5, delay: delay + 0.5 }}
        >
          <Typography
            sx={{
              color: "#546e7a",
              fontFamily: "Poppins",
              fontSize: { xs: "13px", md: "14px" },
              lineHeight: 1.7,
              fontWeight: 400,
            }}
          >
            {stat.description}
          </Typography>
        </motion.div>
      </Box>
    </motion.div>
  );
};

export default AboutSec4;